import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Phone, Shield, Users, Zap, TrendingUp, 
  CheckCircle, Play, Menu, 
  X, FileText, Target, Brain, Sparkles,
  LineChart, Activity, Headphones, MessageSquare,
  ArrowRight, Globe, Layers, Cpu, Loader2
} from 'lucide-react';
import { SiHubspot, SiSlack, SiGmail } from 'react-icons/si';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/config';
import { useTheme } from '../zustand/ThemeStore';
import strideLogo from '../assets/Final Logo Used in White Label.png';
import { paymentService, type PaymentRequest, type PricingConfig } from '../services/payment';

export default function LandingPage() {
  const navigate = useNavigate();
  const isDarkMode = useTheme((set)=>set.theme);
  const setIsDarkMode = useTheme((set)=>set.setTheme);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [_, setActiveTestimonial] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    notes: '',
    terms: false
  });

  // Early Access CTA State - Now managed by real-time updates from backend pricing
  const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);
  const [earlyAccessData, setEarlyAccessData] = useState({
    total_purchased: 0,
    remaining_spots: 10,
    max_spots: 10,
    spots_until_price_increase: 10,
    current_price: 97,
    next_price: 197
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  //@ts-ignore
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const floatingParticles = [...Array(20)].map((_, i) => ({
  id: i,
  left: `${(i * 5) % 100}%`,
  top: `${(i * 7 + 10) % 100}%`,
  duration: `${3 + (i % 4)}s`,
  delay: `${(i % 3) * 0.5}s`,
}));

  // Real-time early access count updates - Load pricing from backend
  useEffect(() => {
    // Get initial pricing configuration and count
    const loadInitialData = async () => {
      try {
        setIsLoadingPricing(true);
        
        // Load pricing configuration first
        const pricingData = await paymentService.getPricingConfig();
        setPricingConfig(pricingData);
        
        // Update early access data for backward compatibility
        if (pricingData.available && pricingData.current_tier) {
          setEarlyAccessData({
            total_purchased: pricingData.total_purchased,
            remaining_spots: pricingData.remaining_spots,
            max_spots: pricingData.max_spots,
            spots_until_price_increase: pricingData.current_tier.spots_until_increase,
            current_price: pricingData.current_tier.price,
            next_price: pricingData.next_tier?.price || pricingData.current_tier.price
          });
        } else {
          // Fallback to legacy endpoint if new one fails
          const count = await paymentService.getEarlyAccessCount();
          setEarlyAccessData(count);
        }
      } catch (error) {
        console.error('Error loading pricing data:', error);
        // Fallback to legacy endpoint
        try {
          const count = await paymentService.getEarlyAccessCount();
          setEarlyAccessData(count);
        } catch (fallbackError) {
          console.error('Error loading fallback early access count:', fallbackError);
        }
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadInitialData();

    // Subscribe to real-time updates
    // const unsubscribe = paymentService.subscribeToEarlyAccessUpdates((data) => {
    //   setEarlyAccessData(data);
    // });

    // Cleanup function
    // return () => {
    //   unsubscribe();
    // };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: any) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  // Handle payment redirects
  useEffect(() => {
    const paymentResult = paymentService.handlePaymentRedirect();
    if (paymentResult.status === 'success') {
      // Redirect to thank you page with session ID
      const sessionId = paymentResult.sessionId;
      navigate(`/thank-you?payment=success${sessionId ? `&session_id=${sessionId}` : ''}`, { replace: true });
      
    } else if (paymentResult.status === 'canceled') {
      toast.warning('⚠️ Payment was canceled. Your spot is still available.', {
        duration: 4000,
        position: 'top-center',
      });
      setPaymentMessage('⚠️ Payment was canceled. Your spot is still available.');
      
      // Scroll to CTA section for canceled payments too
      setTimeout(() => {
        const ctaSection = document.getElementById('early');
        if (ctaSection) {
          ctaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [navigate]);

  // Handle early access spot reservation with payment - Using backend price ID
  const handleReserveSpot = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (earlyAccessData.remaining_spots <= 0) {
      setPaymentMessage('❌ Sorry, all early access spots are taken!');
      return;
    }
    
    // Check if we have pricing configuration
    if (!pricingConfig || !pricingConfig.available || !pricingConfig.current_tier) {
      setPaymentMessage('❌ Pricing information not available. Please refresh the page.');
      return;
    }
    
    try {
      setIsProcessingPayment(true);
      setPaymentMessage('🔄 Creating secure checkout session...');

      // Use pricing configuration from backend
      const customerData: PaymentRequest = {
        price_id: pricingConfig.current_tier.price_id,
        customer_email: 'customer@example.com', // Stripe will collect actual email
        customer_name: 'Customer', // Stripe will collect actual name
      };

      // Create checkout session using the payment service
      const checkoutData = await paymentService.createCheckoutSession(customerData);
      
      // Redirect to Stripe checkout immediately
      setPaymentMessage('🚀 Redirecting to secure payment...');
      window.location.href = checkoutData.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentMessage('❌ Unable to start checkout. Please try again or contact support.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Demo function to fill form with sample data for testing
  // const fillDemoData = () => {
  //   setFormData({
  //     firstName: 'John',
  //     lastName: 'Smith',
  //     company: 'TechCorp Inc.',
  //     email: 'john.smith@techcorp.com',
  //     phone: '+1 (555) 123-4567',
  //     terms: true
  //   });
  // };

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Advanced machine learning algorithms analyze every call for deeper insights",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Target,
      title: "Performance Scoring",
      description: "Comprehensive scoring across 10 key dimensions of sales excellence",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: LineChart,
      title: "Real-time Analytics",
      description: "Track performance trends and identify improvement opportunities instantly",
      gradient: "from-[#1a9f6b] to-[#148856]"
    },
    {
      icon: Users,
      title: "Team Management",
      description: "Monitor and coach your entire sales team from a unified dashboard",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: FileText,
      title: "Detailed Transcripts",
      description: "Full conversation transcripts with AI-generated insights and timestamps",
      gradient: "from-indigo-500 to-purple-500"
    },
    {
      icon: Zap,
      title: "Instant Recommendations",
      description: "Get actionable coaching points and improvement strategies automatically",
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  const stats = [
    { value: "98%", label: "Accuracy Rate", icon: Target },
    { value: "3.2x", label: "Faster Response Rate", icon: Zap },
    { value: "47%", label: "Performance Boost", icon: TrendingUp },
    { value: "10k+", label: "Calls Analyzed", icon: Phone }
  ];

  // const testimonials = [
  //   {
  //     name: "Sarah Johnson",
  //     role: "Sales Director, TechCorp",
  //     content: "Stride has transformed how we coach our sales team. The AI insights are incredibly accurate and have helped us increase our close rate by 35%.",
  //     avatar: "SJ"
  //   },
  //   {
  //     name: "Michael Chen",
  //     role: "VP of Sales, GlobalSoft",
  //     content: "The real-time analytics and performance tracking have given us unprecedented visibility into our sales process. It's a game-changer.",
  //     avatar: "MC"
  //   },
  //   {
  //     name: "Emily Rodriguez",
  //     role: "Sales Manager, InnovateCo",
  //     content: "Our reps love the instant feedback and coaching recommendations. Training time has been cut in half while performance has soared.",
  //     avatar: "ER"
  //   }
  // ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isDarkMode ? 'bg-black' : 'bg-gray-50'
    }`}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? isDarkMode 
            ? 'bg-black/90 backdrop-blur-xl border-b border-[#1a2424]' 
            : 'bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm'
          : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className={`flex items-center  ${isDarkMode ? "bg-white": ""} gap-2 h-16`}>
              <img 
                src={strideLogo} 
                alt="Stride Logo" 
                className="h-24 w-auto"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className={`hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>Features</a>
              <a href="#how-it-works" className={`hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>How it Works</a>
              <a href="#integrations" className={`hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>Integrations</a>
              {/* <a href="#testimonials" className={`hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-300' : 'text-[#373737]'
              }`}>Testimonials</a> */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-[#0a1414] border border-[#1a2424]' 
                    : 'bg-gray-200 border border-gray-300'
                }`}
              >
                <div className={`absolute top-0.5 w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
                  isDarkMode 
                    ? 'left-7 bg-gradient-to-r from-[#1a9f6b] to-[#148856]' 
                    : 'left-0.5 bg-white shadow-md'
                }`}>
                  {isDarkMode ? (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
              <button
                onClick={() => navigate('/auth')}
                className={`px-4 py-2 rounded-full border transition-all hover:scale-105 ${
                  isDarkMode 
                    ? 'border-[#1a2424] text-gray-300 hover:border-[#1a9f6b] hover:text-[#1a9f6b]' 
                    : 'border-gray-300 text-[#373737] hover:border-[#1a9f6b] hover:text-[#1a9f6b]'
                }`}
              >
                Login
              </button>
              <a href="#register" className="px-6 py-2 border-2 border-[#1a9f6b] text-[#1a9f6b] rounded-full hover:bg-[#1a9f6b] hover:text-white transition-all transform hover:scale-105 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Book Demo
              </a>
              <a href="#early" className="px-6 py-2 bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white rounded-full hover:shadow-lg hover:shadow-[#1a9f6b]/30 transition-all transform hover:scale-105">
                Get Started
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? (
                <X className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden absolute top-16 left-0 right-0 ${
            isDarkMode ? 'bg-black/95 backdrop-blur-xl' : 'bg-white/95 backdrop-blur-xl'
          } border-b ${isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'}`}>
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>Features</a>
              <a href="#how-it-works" className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>How it Works</a>
              <a href="#integrations" className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>Integrations</a>
              <a href="#testimonials" className={`block py-2 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>Testimonials</a>
              
              <div className={`flex items-center justify-between py-2 border-t ${isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'}`}>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Theme</span>
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-[#0a1414] border border-[#1a2424]' 
                      : 'bg-gray-200 border border-gray-300'
                  }`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
                    isDarkMode 
                      ? 'left-7 bg-gradient-to-r from-[#1a9f6b] to-[#148856]' 
                      : 'left-0.5 bg-white shadow-md'
                  }`}>
                    {isDarkMode ? (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => navigate('/auth')}
                className={`w-full py-3 border rounded-lg text-center transition-all mb-2 ${
                  isDarkMode 
                    ? 'border-[#1a2424] text-gray-300 hover:border-[#1a9f6b] hover:text-[#1a9f6b]' 
                    : 'border-gray-300 text-[#373737] hover:border-[#1a9f6b] hover:text-[#1a9f6b]'
                }`}
              >
                Login
              </button>
              
              <a href="#register" className="w-full py-3 mb-2 border-2 border-[#1a9f6b] text-[#1a9f6b] rounded-lg text-center font-semibold flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Book a Demo
              </a>
              
              <a href="#early" className="w-full py-3 bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white rounded-lg text-center font-semibold flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />
                Get Started
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Promotional banner removed — showing price-only CTA elsewhere */}

      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen flex items-center px-4 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl animate-pulse ${
            isDarkMode ? 'bg-[#1a9f6b]/20' : 'bg-[#1a9f6b]/10'
          }`} />
          <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl animate-pulse delay-1000 ${
            isDarkMode ? 'bg-[#148856]/20' : 'bg-[#148856]/10'
          }`} />
          <div className={`absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse delay-500 ${
            isDarkMode ? 'bg-[#117a47]/10' : 'bg-[#117a47]/5'
          }`} />
          
          {/* Grid pattern overlay */}
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-[linear-gradient(rgba(26,159,107,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(26,159,107,0.03)_1px,transparent_1px)]' 
              : 'bg-[linear-gradient(rgba(26,159,107,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(26,159,107,0.05)_1px,transparent_1px)]'
          }`} style={{ backgroundSize: '50px 50px' }} />
        </div>

        <div className="max-w-7xl mx-auto w-full relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 bg-gradient-to-r from-[#1a9f6b]/10 to-[#148856]/10 border border-[#1a9f6b]/30">
                <Sparkles className="w-3.5 h-3.5 text-[#1a9f6b]" />
                <span className="text-[#1a9f6b] text-xs font-semibold">AI-Powered Call Intelligence</span>
              </div>
              
              <h1 className={`text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>
                Analyze every call,
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1a9f6b] to-[#148856]">
                  accelerate sales success
                </span>
              </h1>
              
              <p className={`text-base md:text-lg mb-4 ${
                isDarkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                Turn every sales conversation into actionable insights. AI-powered analysis that scores calls, identifies opportunities, and delivers coaching recommendations.
              </p>

              {/* <p className={`text-sm md:text-base mb-6 ${
                isDarkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                Everything you need to coach your team and close more deals — automated transcription, AI scoring, and performance analytics in one platform.
              </p> */}

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <a href="#early" className="group relative px-8 py-4 bg-gradient-to-r from-[#1a9f6b] via-[#148856] to-[#117a47] text-white rounded-full text-lg font-bold hover:shadow-2xl hover:shadow-[#1a9f6b]/50 transition-all transform hover:scale-105 flex items-center justify-center gap-2 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#148856] via-[#1a9f6b] to-[#148856] opacity-0 group-hover:opacity-100 transition-opacity" />
                  <Sparkles className="w-5 h-5 relative z-10 animate-pulse" />
                  <span className="relative z-10">Start at $97/mo →</span>
                </a>
                <a href="#register" className={`px-6 py-4 rounded-full text-lg font-semibold border-2 transition-all flex items-center justify-center gap-2 hover:scale-105 ${
                  isDarkMode 
                    ? 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white' 
                    : 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white'
                }`}>
                  <Play className="w-4 h-4" />
                  Watch Demo
                </a>
              </div>
              

              {/* <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                Trusted by leading sales teams worldwide
              </p> */}
            </div>

            {/* Right Side - Video Demo */}
            <div className="relative">
              <div 
                className={`relative rounded-2xl overflow-hidden shadow-2xl border cursor-pointer group ${
                  isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
                }`}
                onClick={() => setIsVideoModalOpen(true)}
              >
                {/* Glow Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#1a9f6b] to-[#148856] opacity-20 blur-xl" />
                
                {/* Play Icon Overlay */}
                <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                  <div className="w-20 h-20 rounded-full bg-[#1a9f6b] flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </div>
                
                {/* Video Wrapper */}
                <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/wV0N3rxeWno?si=wFOhRcwttjBcWgT7&autoplay=1&mute=1&loop=1&playlist=wV0N3rxeWno"
                    title="Stride Sales Coach Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-[#0a1414]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#1a9f6b]/20 to-[#148856]/20 mb-4">
                  <stat.icon className="w-8 h-8 text-[#1a9f6b]" />
                </div>
                <div className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  {stat.value}
                </div>
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Powerful Features for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1a9f6b] to-[#148856]"> Modern Sales Teams</span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-400' : 'text-[#373737]'
            }`}>
              Everything you need to analyze, coach, and optimize your sales conversations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative rounded-2xl p-8 transition-all duration-300 hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#0a1414] to-[#050a0a] border border-[#1a2424] hover:border-[#1a9f6b]' 
                    : 'bg-white border border-gray-200 hover:border-[#1a9f6b] shadow-lg hover:shadow-xl'
                }`}
              >
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
                <div className={`relative z-10`}>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} mb-6`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-[#053333]'
                  }`}>
                    {feature.title}
                  </h3>
                  <p className={`${
                    isDarkMode ? 'text-gray-400' : 'text-[#373737]'
                  }`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className={`py-20 px-4 ${
        isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              How It Works
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-400' : 'text-[#373737]'
            }`}>
              Get started in minutes and see results immediately
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-[#1a9f6b] to-[#148856]" />
            
            {[
              { icon: Headphones, title: "Record Calls", description: "Automatically capture and upload your sales calls" },
              { icon: Cpu, title: "AI Analysis", description: "Our AI analyzes conversations across multiple dimensions" },
              { icon: Activity, title: "Get Insights", description: "Receive detailed reports and actionable coaching points" }
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className={`rounded-2xl p-8 text-center ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-[#050a0a] to-black border border-[#1a2424]' 
                    : 'bg-white border border-gray-200'
                }`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#1a9f6b] to-[#148856] mb-6">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className={`absolute -top-4 left-8 w-8 h-8 rounded-full bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white font-bold flex items-center justify-center`}>
                    {index + 1}
                  </div>
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-[#053333]'
                  }`}>
                    {step.title}
                  </h3>
                  <p className={`${
                    isDarkMode ? 'text-gray-400' : 'text-[#373737]'
                  }`}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations & Analytics Section */}
      <section id="integrations" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Seamless Integrations &
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1a9f6b] to-[#148856]"> Powerful Analytics</span>
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-400' : 'text-[#373737]'
            }`}>
              Connect with your favorite tools and unlock deep insights with our analytics dashboard
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Integrations Side */}
            <div>
              <h3 className={`text-2xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>
                Works Where You Work
              </h3>
              <p className={`mb-8 ${
                isDarkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                Stride integrates seamlessly with your existing tech stack, ensuring smooth workflow and data synchronization across all platforms.
              </p>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { name: 'HubSpot', Icon: SiHubspot, color: 'from-orange-500 to-red-500' },
                  { name: 'Slack', Icon: SiSlack, color: 'from-purple-500 to-pink-500' },
                  { name: 'Gmail', Icon: SiGmail, color: 'from-red-500 to-pink-500' },
                ].map((integration, index) => (
                  <div
                    key={index}
                    className={`group relative rounded-xl p-4 text-center transition-all duration-300 hover:scale-110 ${
                      isDarkMode 
                        ? 'bg-[#0a1414] border border-[#1a2424] hover:border-[#1a9f6b]' 
                        : 'bg-white border border-gray-200 hover:border-[#1a9f6b] shadow-md'
                    }`}
                  >
                    <div className={`flex justify-center mb-2`}>
                      <integration.Icon className="w-8 h-8 text-gray-600" />
                    </div>
                    <p className={`text-xs font-medium ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {integration.name}
                    </p>
                    <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${integration.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                  </div>
                ))}
              </div>

              <div className={`rounded-xl p-6 ${
                isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#1a9f6b] to-[#148856] rounded-lg flex items-center justify-center">
                    <Layers className="w-5 h-5 text-white" />
                  </div>
                  <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    API & Webhooks
                  </h4>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Build custom integrations with our comprehensive REST API and real-time webhooks for seamless data flow.
                </p>
              </div>
            </div>

            {/* Analytics Dashboard Preview */}
            <div className={`relative rounded-2xl overflow-hidden shadow-2xl border ${
              isDarkMode ? 'border-[#1a2424] bg-[#0a1414]' : 'border-gray-200 bg-white'
            }`}>
              <div className="p-6">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between mb-6">
                  <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    Analytics Dashboard
                  </h4>
                  <div className="flex gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  </div>
                </div>

                {/* Mini Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Phone className="w-4 h-4 text-[#1a9f6b]" />
                      <span className="text-xs text-green-500">+12%</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>2,847</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Calls</p>
                  </div>
                  <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-4 h-4 text-[#1a9f6b]" />
                      <span className="text-xs text-green-500">+8%</span>
                    </div>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>8.4</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Avg Score</p>
                  </div>
                </div>

                {/* Performance Chart */}
                <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'}`}>
                  <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Team Performance Trend
                  </p>
                  <div className="space-y-2">
                    {['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{week}</span>
                          <span className={isDarkMode ? 'text-white' : 'text-[#053333]'}>{75 + i * 5}%</span>
                        </div>
                        <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-200'}`}>
                          <div 
                            className="h-2 bg-gradient-to-r from-[#1a9f6b] to-[#148856] rounded-full transition-all duration-1000"
                            style={{width: `${75 + i * 5}%`}}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Activity */}
                <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                  isDarkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                }`}>
                  <div className="relative">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-ping absolute"></div>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                    Live: 12 calls being analyzed
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Section */}
          {/* <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: PieChart, title: "Conversion Rate", value: "32%", change: "+5.2%", trend: "up" },
              { icon: Activity, title: "Call Quality", value: "8.7/10", change: "+0.8", trend: "up" },
              { icon: Clock, title: "Avg Call Time", value: "24m", change: "-2m", trend: "down" },
              { icon: Award, title: "Success Rate", value: "89%", change: "+12%", trend: "up" }
            ].map((metric, index) => (
              <div
                key={index}
                className={`rounded-xl p-6 transition-all hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-[#0a1414] border border-[#1a2424]' 
                    : 'bg-white border border-gray-200 shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
                  }`}>
                    <metric.icon className="w-5 h-5 text-[#1a9f6b]" />
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    metric.trend === 'up' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <p className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  {metric.value}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {metric.title}
                </p>
              </div>
            ))}
          </div> */}
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section id="testimonials" className={`py-20 px-4 ${
        isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Loved by Sales Teams Everywhere
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-gray-400' : 'text-[#373737]'
            }`}>
              See what our customers have to say about Stride
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className={`rounded-2xl p-8 md:p-12 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#050a0a] to-black border border-[#1a2424]' 
                : 'bg-white border border-gray-200 shadow-xl'
            }`}>
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              
              <p className={`text-xl mb-8 italic ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                "{testimonials[activeTestimonial].content}"
              </p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1a9f6b] to-[#148856] rounded-full flex items-center justify-center text-white font-bold">
                  {testimonials[activeTestimonial].avatar}
                </div>
                <div>
                  <p className={`font-semibold ${
                    isDarkMode ? 'text-white' : 'text-[#053333]'
                  }`}>
                    {testimonials[activeTestimonial].name}
                  </p>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {testimonials[activeTestimonial].role}
                  </p>
                </div>
              </div>


              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === activeTestimonial
                        ? 'w-8 bg-[#1a9f6b]'
                        : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Pricing CTA Section */}

<section id="early" className={`py-20 px-4 relative overflow-hidden ${
  isDarkMode ? 'bg-gradient-to-b from-black via-[#0a1414] to-black' : 'bg-gradient-to-br from-[#e6f7f0] via-[#d1f0e3] to-[#bce9d6]'
}`}>
  {/* Animated Background Elements */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl animate-pulse ${
      isDarkMode ? 'bg-[#1a9f6b]/10' : 'bg-[#1a9f6b]/20'
    }`} />
    <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl animate-pulse delay-1000 ${
      isDarkMode ? 'bg-[#148856]/10' : 'bg-[#148856]/20'
    }`} />
    <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl animate-pulse delay-500 ${
      isDarkMode ? 'bg-[#117a47]/5' : 'bg-[#117a47]/10'
    }`} />
    
    {/* Floating particles - using stable values */}
    {floatingParticles.map((particle) => (
      <div
        key={particle.id}
        className={`absolute w-1 h-1 rounded-full ${
          isDarkMode ? 'bg-[#1a9f6b]/30' : 'bg-[#1a9f6b]/40'
        }`}
        style={{
          left: particle.left,
          top: particle.top,
          animation: `float ${particle.duration} ease-in-out infinite`,
          animationDelay: particle.delay,
        }}
      />
    ))}
  </div>
  
  <div className="max-w-5xl mx-auto text-center relative z-10">
    {/* Animated Badge */}
    <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#1a9f6b]/20 via-[#148856]/20 to-[#117a47]/20 border border-[#1a9f6b]/50">
      <Zap className="w-4 h-4 text-[#1a9f6b] animate-bounce" />
      <span className={`text-sm font-bold ${
        isDarkMode ? 'text-[#1a9f6b]' : 'text-[#117a47]'
      }`}>EARLY ADOPTER PRICING</span>
      <Sparkles className="w-4 h-4 text-[#1a9f6b] animate-pulse" />
    </div>
    
    <h2 className={`text-4xl md:text-6xl font-black mb-4 leading-tight ${
      isDarkMode ? 'text-white' : 'text-gray-900'
    }`}>
      Transform Your Sales Team
      <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-[#1a9f6b] via-[#148856] to-[#117a47] animate-gradient">
        Starting at Just $97/mo
      </span>
    </h2>
    
    <p className={`text-lg md:text-xl mb-8 max-w-2xl mx-auto ${
      isDarkMode ? 'text-gray-300' : 'text-gray-700'
    }`}>
      Get enterprise-level AI call analysis at a fraction of the cost. Lock in your rate today.
    </p>

    {/* Two Card Comparison */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
      
      {/* Pricing Card - $97 CTA - FIXED with group wrapper */}
      <div className="relative group">
        {/* Badge - outside scaling container */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-r from-[#1a9f6b] via-[#148856] to-[#117a47] text-white px-6 py-2 rounded-full text-sm font-black shadow-lg flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          BEST VALUE
        </div>

        <button 
          onClick={handleReserveSpot}
          disabled={isProcessingPayment}
          className={`w-full p-10 pt-12 rounded-3xl border-4 shadow-xl transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-[#0a3d2a]/40 via-[#0d4d34]/40 to-[#115c3f]/40 border-[#1a9f6b]' 
              : 'bg-gradient-to-br from-[#e6f7f0] via-[#d1f0e3] to-[#bce9d6] border-[#1a9f6b]'
          } ${isProcessingPayment ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="text-center">
            <p className={`text-sm font-bold uppercase mb-4 ${
              isDarkMode ? 'text-[#1a9f6b]' : 'text-[#148856]'
            }`}>
              Early Adopter Rate
            </p>
            
            <div className={`text-7xl md:text-8xl font-black mb-6 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              $97
              <span className="text-2xl md:text-3xl">/mo</span>
            </div>
            
            <div className={`space-y-2 mb-6 text-left px-4 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {['Unlimited call analysis', 'AI-powered insights', 'Performance tracking', 'Priority support'].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#1a9f6b] flex-shrink-0" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <div className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-lg ${
              isDarkMode ? 'bg-[#1a9f6b] text-white' : 'bg-[#148856] text-white'
            }`}>
              <Zap className="w-5 h-5" />
              {isProcessingPayment ? 'Processing...' : 'Get Started Now'}
              <ArrowRight className="w-5 h-5" />
            </div>
            
            <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
              🔒 Cancel anytime • No hidden fees
            </p>
          </div>
        </button>
      </div>

      {/* Regular Price Card - $197+ */}
      <div className={`relative p-10 rounded-3xl border-2 opacity-60 ${
        isDarkMode 
          ? 'bg-gray-900/50 border-gray-700' 
          : 'bg-gray-100 border-gray-300'
      }`}>
        {/* Badge */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className={`px-6 py-2 rounded-full text-sm font-bold ${
            isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-300 text-gray-600'
          }`}>
            Regular Price
          </div>
        </div>

        <div className="text-center pt-6">
          <p className={`text-sm font-bold uppercase mb-2 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            Standard Rate
          </p>
          
          <div className="relative mb-6">
            <div className={`text-7xl md:text-8xl font-black relative ${
              isDarkMode ? 'text-gray-600' : 'text-gray-400'
            }`}>
              $197
              <span className="text-2xl md:text-3xl line-through">+</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-1 bg-red-500 transform rotate-12" />
            </div>
          </div>

          {/* What you miss */}
          <div className={`space-y-2 mb-6 text-left px-4 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {['Same call analysis', 'Same AI features', 'Same support', 'Just costs more'].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <X className="w-5 h-5 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium line-through">{feature}</span>
              </div>
            ))}
          </div>

          <p className={`text-lg font-bold ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            Save $1,200/year
          </p>
          
          <p className={`text-sm mt-2 ${
            isDarkMode ? 'text-gray-600' : 'text-gray-500'
          }`}>
            with early adopter pricing
          </p>
        </div>
      </div>
    </div>

    {/* Main CTA Button - FIXED with group wrapper */}
    <div className="mb-8">
      <div className="inline-block group">
        <button 
          onClick={handleReserveSpot}
          disabled={isProcessingPayment}
          className={`relative inline-flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-[#1a9f6b] via-[#148856] to-[#117a47] text-white rounded-full text-xl md:text-2xl font-black shadow-xl transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-[#1a9f6b]/40 ${
            isProcessingPayment ? 'opacity-75 cursor-not-allowed' : ''
          }`}
          style={{ minWidth: '320px' }}
        >
          {isProcessingPayment ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Processing Payment...</span>
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-r from-[#148856] via-[#1a9f6b] to-[#148856] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
              <Sparkles className="w-7 h-7 relative z-10 animate-pulse" />
              <span className="relative z-10">Start Your Journey</span>
              <ArrowRight className="w-7 h-7 relative z-10 group-hover:translate-x-2 transition-transform duration-300" />
            </>
          )}
        </button>
      </div>
      
      {paymentMessage && (
        <div className={`mt-4 p-4 rounded-xl text-sm font-semibold ${
          paymentMessage.includes('successful') 
            ? isDarkMode ? 'bg-green-900/30 text-green-300 border-2 border-green-600' : 'bg-green-50 text-green-700 border-2 border-green-500'
            : paymentMessage.includes('failed') || paymentMessage.includes('canceled')
            ? isDarkMode ? 'bg-red-900/30 text-red-300 border-2 border-red-600' : 'bg-red-50 text-red-700 border-2 border-red-500'
            : isDarkMode ? 'bg-blue-900/30 text-blue-300 border-2 border-blue-600' : 'bg-blue-50 text-blue-700 border-2 border-blue-500'
        }`}>
          {paymentMessage}
        </div>
      )}
    </div>

    {/* Alternative CTA - Book Demo - FIXED */}
    <div className={`mt-8 pt-8 border-t ${isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'}`}>
      <p className={`text-center text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Not ready to commit? See Stride in action first.
      </p>
      <div className="flex justify-center">
        <div className="inline-block group">
          <a 
            href="#register" 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold border-2 transform-gpu transition-transform duration-300 ease-out group-hover:scale-105 ${
              isDarkMode 
                ? 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white' 
                : 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white'
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            Book a Demo
          </a>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Registration Form Section */}
      <section id="register" className={`py-20 px-4 ${
        isDarkMode ? 'bg-[#0a1414]' : 'bg-gray-50'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Benefits */}
            <div>
              <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>
                Start Your Journey to
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1a9f6b] to-[#148856]"> Sales Excellence</span>
              </h2>
              <p className={`text-xl mb-8 ${
                isDarkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                Join leading companies transforming their sales performance with AI-powered call analytics
              </p>

              {/* Benefits List */}
              <div className="space-y-4 mb-8">
                {[
                  { icon: Users, text: "Onboard your entire team in minutes" },
                  { icon: Shield, text: "Enterprise-grade security & compliance" },
                  { icon: Headphones, text: "24/7 dedicated customer support" }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <benefit.icon className="w-5 h-5 text-[#1a9f6b] mt-0.5 flex-shrink-0" />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {benefit.text}
                    </span>
                  </div>
                ))}
              </div>

            </div>

            {/* Right Side - Registration Form */}
            <div className={`relative rounded-2xl p-8 shadow-2xl ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#050a0a] to-black border border-[#1a2424]' 
                : 'bg-white border border-gray-200'
            }`}>
              {/* Loading Overlay */}
              {isSubmittingForm && (
                <div className={`absolute inset-0 rounded-2xl flex items-center justify-center z-10 ${
                  isDarkMode ? 'bg-black/70' : 'bg-white/70'
                } backdrop-blur-sm`}>
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-[#1a9f6b] mx-auto mb-3" />
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      Sending your enquiry...
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Please wait a moment
                    </p>
                  </div>
                </div>
              )}
              
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1a9f6b] to-[#148856] rounded-full mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-[#053333]'
                }`}>
                  Send Inquiry
                </h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Get in touch with us to learn more about Stride
                </p>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                
                setIsSubmittingForm(true);
                setPaymentMessage(''); // Clear any previous messages
                
                try {
                  // Submit form data to backend for lead collection only
                  const response = await fetch(`${getApiBaseUrl()}/api/contact/submit`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                  });
                  
                  if (response.ok) {
                    const result = await response.json();
                    console.log('Form submitted successfully:', result);
                    setFormSubmitted(true);
                    setPaymentMessage('✅ Thank you! Your enquiry has been sent successfully.');
                    
                    // Reset form after 3 seconds
                    setTimeout(() => {
                      setFormSubmitted(false);
                      setPaymentMessage('');
                      setFormData({
                        firstName: '',
                        lastName: '',
                        company: '',
                        email: '',
                        phone: '',
                        notes: '',
                        terms: false
                      });
                    }, 3000);
                    
                  } else {
                    const error = await response.json();
                    console.error('Form submission failed:', error);
                    setPaymentMessage('❌ Form submission failed. Please try again.');
                  }
                } catch (error) {
                  console.error('Error submitting form:', error);
                  setPaymentMessage('❌ An error occurred. Please check your connection and try again.');
                } finally {
                  setIsSubmittingForm(false);
                }
              }} className="space-y-4">
                {formSubmitted ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-4">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h4 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      Inquiry Sent Successfully!
                    </h4>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Thank you for your enquiry. We'll get back to you shortly!
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Name Fields Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${
                          isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                        }`}>
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          disabled={isSubmittingForm}
                          className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${
                          isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                        }`}>
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          disabled={isSubmittingForm}
                          className={`w-full px-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Company Name */}
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                      }`}>
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Globe className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <input
                          type="text"
                          required
                          placeholder="Acme Corporation"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                          disabled={isSubmittingForm}
                          className={`w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Company Email */}
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                      }`}>
                        Company Email <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <input
                          type="email"
                          required
                          placeholder="john.doe@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          disabled={isSubmittingForm}
                          className={`w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Notes/Description Field */}
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                      }`}>
                        Notes/Description <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(Optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-3">
                          <FileText className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <textarea
                          placeholder="Tell us about your requirements, team size, or any specific questions..."
                          value={formData.notes}
                          onChange={(e) => setFormData({...formData, notes: e.target.value})}
                          disabled={isSubmittingForm}
                          rows={4}
                          className={`w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent resize-none ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Phone Number (Optional) */}
                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${
                        isDarkMode ? 'text-gray-300' : 'text-[#373737]'
                      }`}>
                        Phone Number <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>(Optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Phone className={`w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <input
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          disabled={isSubmittingForm}
                          className={`w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent ${
                            isDarkMode
                              ? 'bg-[#0a1414] border-[#1a2424] text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                          } ${isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Terms Checkbox */}
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        required
                        id="terms"
                        checked={formData.terms}
                        onChange={(e) => setFormData({...formData, terms: e.target.checked})}
                        disabled={isSubmittingForm}
                        className={`mt-1 w-4 h-4 text-[#1a9f6b] border-gray-300 rounded focus:ring-[#1a9f6b] ${
                          isSubmittingForm ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                      <label htmlFor="terms" className={`text-sm ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        I agree to Stride's{' '}
                        <Link to="/terms-of-service" className="text-[#1a9f6b] hover:underline">Terms of Service</Link>
                        {' '}and{' '}
                        <Link to="/privacy-policy" className="text-[#1a9f6b] hover:underline">Privacy Policy</Link>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmittingForm}
                      className={`group w-full py-4 bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white rounded-lg font-semibold text-lg hover:shadow-2xl hover:shadow-[#1a9f6b]/40 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 ${
                        isSubmittingForm ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmittingForm && (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      )}
                      {isSubmittingForm ? 'Sending...' : 'Send Inquiry'}
                      {!isSubmittingForm && (
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      )}
                    </button>

                    {/* Payment Message */}
                    {paymentMessage && (
                      <div className={`mt-3 p-3 rounded-lg text-sm text-center ${
                        paymentMessage.includes('successful') 
                          ? isDarkMode ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-green-50 text-green-700 border border-green-200'
                          : paymentMessage.includes('failed') || paymentMessage.includes('canceled')
                          ? isDarkMode ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'
                          : isDarkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        {paymentMessage}
                      </div>
                    )}

                    {/* Social Proof */}
                    <div className={`text-center pt-4 border-t ${
                      isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
                    }`}>
                      <p className={`text-xs ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        🔒 Your data is secure and encrypted • 🚀 Setup takes less than 2 minutes
                      </p>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Additional CTA Section */}
      <section className={`py-20 px-4 ${
        isDarkMode ? 'bg-gradient-to-r from-[#0a1414] via-black to-[#0a1414]' : 'bg-gradient-to-r from-gray-50 via-white to-gray-50'
      }`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-gradient-to-r from-[#1a9f6b]/10 to-[#148856]/10 border border-[#1a9f6b]/30">
            <Headphones className="w-4 h-4 text-[#1a9f6b]" />
            <span className="text-[#1a9f6b] text-sm font-semibold">Expert Support Team</span>
          </div>
          
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            Have Questions? We're Here to Help
          </h2>
          <p className={`text-lg mb-8 ${
            isDarkMode ? 'text-gray-400' : 'text-[#373737]'
          }`}>
            Our team is ready to show you how Stride can transform your sales operations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href='#register' 
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#1a9f6b] to-[#148856] text-white rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-[#1a9f6b]/40 transition-all transform hover:scale-105"
            >
              <MessageSquare className="w-5 h-5" />
              Book a Demo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="#early" 
              className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-lg font-semibold border-2 transition-all transform hover:scale-105 ${
                isDarkMode 
                  ? 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white' 
                  : 'border-[#1a9f6b] text-[#1a9f6b] hover:bg-[#1a9f6b] hover:text-white'
              }`}
            >
              <Zap className="w-5 h-5" />
              Get Started Now
            </a>
          </div>
          
          <div className="mt-8 flex flex-wrap justify-center items-center gap-6">
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <CheckCircle className="w-5 h-5 text-[#1a9f6b]" />
              <span className="text-sm">No credit card required</span>
            </div>
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <CheckCircle className="w-5 h-5 text-[#1a9f6b]" />
              <span className="text-sm">15-minute demo</span>
            </div>
            <div className={`flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <CheckCircle className="w-5 h-5 text-[#1a9f6b]" />
              <span className="text-sm">Instant setup</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`border-t py-12 px-4 ${
        isDarkMode 
          ? 'bg-black border-[#1a2424]' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
            <div>
              <div className={`flex items-center rounded-md w-fit gap-2 mb-4 ${isDarkMode ? "bg-white":"bg-white/10"}`}>
                <img 
                  src={strideLogo} 
                  alt="Stride Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                AI-powered call analysis for modern sales teams
              </p>
            </div>
            
            <div>
              <h4 className={`font-semibold mb-4 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>Product</h4>
              <div className="space-y-2">
                <a href="#features" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Features</a>
                <a href="#integrations" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Integrations</a>
                {/* <a href="#" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Analytics</a> */}
              </div>
            </div>
            
            <div>
              <h4 className={`font-semibold mb-4 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>Company</h4>
              <div className="space-y-2">
                <a href="https://stridecrm.co/" target="_blank" rel="noopener noreferrer" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>About</a>
                {/* <a href="#" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Blog</a>
                <a href="#" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Careers</a> */}
              </div>
            </div>
            
            <div>
              <h4 className={`font-semibold mb-4 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>Other Products</h4>
              <div className="space-y-2">
                <a href="https://stridecrm.co/" target="_blank" rel="noopener noreferrer" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Stride CRM</a>
                <a href="https://strideskip.co/" target="_blank" rel="noopener noreferrer" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Stride Skip</a>
              </div>
            </div>
            
            <div>
              <h4 className={`font-semibold mb-4 ${
                isDarkMode ? 'text-white' : 'text-[#053333]'
              }`}>Support</h4>
              <div className="space-y-2">
                {/* <a href="#" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Documentation</a> */}
                <a href="mailto:rohit@landbh.com" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Contact</a>
                {/* <a href="#" className={`block text-sm hover:text-[#1a9f6b] transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Status</a> */}
              </div>
            </div>
          </div>
          
          <div className={`border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4 ${
            isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
          }`}>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-600'
            }`}>
              © 2025 Stride. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className={`text-sm hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-500' : 'text-gray-600'
              }`}>Privacy Policy</Link>
              <Link to="/terms-of-service" className={`text-sm hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-500' : 'text-gray-600'
              }`}>Terms of Service</Link>
              <Link to="/cookie-policy" className={`text-sm hover:text-[#1a9f6b] transition-colors ${
                isDarkMode ? 'text-gray-500' : 'text-gray-600'
              }`}>Cookie Policy</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={() => setIsVideoModalOpen(false)}
        >
          {/* Blurred Background Overlay */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          
          {/* Modal Content */}
          <div 
            className="relative z-10 w-full max-w-6xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#1a9f6b] transition-colors p-2 rounded-full bg-black/50 hover:bg-black/70"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Video Container */}
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-[#1a9f6b]/30">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/wV0N3rxeWno?si=wFOhRcwttjBcWgT7&autoplay=1"
                title="Stride Sales Coach Demo - Full Screen"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}